import { json } from "@remix-run/node";

import {
  getCurrentContent,
  createSeoMetaData,
  getArtworkFromData,
} from "~/utils/data";
import { Action } from "~/constants";
import {
  bookmarkModule,
  getModuleByPosition,
  unbookmarkModule,
} from "~/models/module.server";
import {
  bookmarkSession,
  unbookmarkSession,
  markSessionComplete,
  unmarkSessionComplete,
} from "~/models/session.server";

import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaArgs,
} from "@remix-run/node";
import type {
  Session,
  Module,
  ClassroomQuiz,
  Scalars,
  ClassroomImage,
} from "~/graphql/types";
import type { CurrentContent } from "~/utils/data";

export interface ModuleRouteLoaderData {
  module: Module;
  currentContent: CurrentContent;
}

export async function loader({ params, context, request }: LoaderFunctionArgs) {
  const position = Number(params.module);

  const module = await getModuleByPosition(
    context.graphql,
    params.class!,
    position,
  );

  if (!module) {
    throw new Response("Not Found", {
      status: 404,
    });
  }

  const moduleContent = ([] as (Session | ClassroomQuiz)[]).concat(
    module.sessions?.nodes ?? [],
    module.quiz ?? [],
  );

  // Determine the current content
  const currentContent = getCurrentContent(moduleContent, {
    currentSession: module.class?.user?.currentSession,
  });

  return json<ModuleRouteLoaderData>({
    module,
    currentContent,
  });
}

export async function action({ request, context }: ActionFunctionArgs) {
  const formData = await request.formData();
  const intent = formData.get("intent");

  switch (intent) {
    case Action.BookmarkModule:
      return await bookmarkModule(
        context.graphql,
        formData.get("moduleId") as string,
      );
    case Action.UnbookmarkModule:
      return await unbookmarkModule(
        context.graphql,
        formData.get("moduleId") as string,
      );
    case Action.BookmarkSession:
      return bookmarkSession(
        context.graphql,
        formData.get("sessionId") as Scalars["ID"]["input"],
      );
    case Action.UnbookmarkSession:
      return unbookmarkSession(
        context.graphql,
        formData.get("sessionId") as Scalars["ID"]["input"],
      );
    case Action.MarkSessionComplete:
      return markSessionComplete(
        context.graphql,
        formData.get("sessionId") as Scalars["ID"]["input"],
      );
    case Action.UnmarkSessionComplete:
      return unmarkSessionComplete(
        context.graphql,
        formData.get("sessionId") as Scalars["ID"]["input"],
      );
    default:
      return {};
  }
}

export function meta({ data }: MetaArgs<typeof loader>) {
  return [
    ...createSeoMetaData({
      title: data?.module.seo?.title,
      description: data?.module.seo?.description,
      image: data?.module.artwork
        ? getArtworkFromData(
            data.module.artwork as unknown as ClassroomImage[],
            "16:9",
          )?.url
        : undefined,
    }),
  ];
}

export { ModuleDetail as default } from "./ModuleDetail";
